
import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import Cookies from "js-cookie"
import {
  filterVisibleFoods,
  formatRemainingTime,
  getFoodImageUrl,
  getFoodStatusLabel,
  isOrderable,
  normalizeCategory
} from "../utils/foodAvailability"

const API = "https://food-backend-9733.onrender.com"

const categoryMeta = {
  cookies: {
    name: "Cookies",
    kicker: "Freshly Baked Cookies",
    description:
      "Delicious homemade cookies prepared in fresh batches for your family."
  },
  snacks: {
    name: "Snacks",
    kicker: "Homemade Crunch",
    description:
      "Fresh snacks, pickles and everyday homemade specials from our kitchen."
  },
  delights: {
    name: "Festival Delights",
    kicker: "Celebration Foods",
    description:
      "Traditional sweets and festive items prepared in fresh batches."
  },
  desserts: {
    name: "Desserts",
    kicker: "Sweet Recipes",
    description:
      "Homemade desserts, sweets and recipe-style specials for family orders."
  },
  mixtures: {
    name: "Mixtures",
    kicker: "Savory Mixes",
    description:
      "Crispy mixtures and snack combinations packed with fresh flavor."
  },
  weekly: {
    name: "Weekly Items",
    kicker: "Weekly Specials",
    description:
      "Items available for one week after upload."
  },
  "48hours": {
    name: "48 Hours Items",
    kicker: "Limited-Time Specials",
    description:
      "Freshly uploaded homemade food items available for 48 hours."
  }
}

const categoryAliases = {
  cookies: ["cookies", "cookie"],
  snacks: ["snacks", "snack", "normal", ""],
  delights: [
    "delights",
    "delight",
    "festival delights",
    "festival-delights"
  ],
  desserts: [
    "desserts",
    "dessert",
    "recipes",
    "recipe",
    "receipts"
  ],
  mixtures: ["mixtures", "mixture"],
  weekly: ["weekly"],
  "48hours": [
    "48hours",
    "48 hours",
    "48-hour",
    "48 hours items"
  ]
}

const nonSnackCategories = [
  "48hours",
  "weekly",
  "cookies",
  "cookie",
  "delights",
  "delight",
  "festival delights",
  "festival-delights",
  "desserts",
  "dessert",
  "recipes",
  "recipe",
  "receipts",
  "mixtures",
  "mixture"
]

const CategoryPage = () => {
  const { category } = useParams()
  const navigate = useNavigate()

  const [foods, setFoods] = useState([])
  const [search, setSearch] = useState("")
  const [cartItems, setCartItems] = useState({})
  const [loadingItemId, setLoadingItemId] = useState(null)

  const getToken = () => {
    try {
      return JSON.parse(Cookies.get("logincrd") || "{}")?.token
    } catch {
      return null
    }
  }

  const loadFoods = async () => {
    try {
      const res = await axios.get(`${API}/foods`)
      setFoods(filterVisibleFoods(res.data))
    } catch (err) {
      console.log("LOAD FOODS ERROR:", err)
    }
  }

  const loadCart = async () => {
    const token = getToken()

    if (!token) {
      setCartItems({})
      return
    }

    try {
      const res = await axios.get(`${API}/cart`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      const obj = {}

      res.data.forEach(item => {
        const key = `${item.food_id}_${item.selected_size || "250g"}`
        obj[key] = item.quantity
      })

      setCartItems(obj)
    } catch (err) {
      console.log("LOAD CART ERROR:", err)
    }
  }

  useEffect(() => {
    loadFoods()
    loadCart()
  }, [])

  const addToCart = async (food_id) => {
    const foodItem = foods.find(
      item => String(item.id) === String(food_id)
    )

    if (!isOrderable(foodItem)) {
      alert("This item is currently out of stock")
      return
    }

    const token = getToken()

    if (!token) {
      navigate("/login")
      return
    }

    if (loadingItemId !== null) return

    setLoadingItemId(food_id)

    try {
      await axios.post(
        `${API}/cart`,
        {
          food_id,
          selected_size: "250g"
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      await loadCart()
      window.dispatchEvent(new Event("srilekha-cart-updated"))
    } catch (err) {
      console.log("ADD CART ERROR:", err.response?.data || err)
      alert(err.response?.data?.msg || "Add to cart failed")
    } finally {
      setLoadingItemId(null)
    }
  }

  const incrementQty = async (food_id) => {
    const token = getToken()

    if (!token) {
      navigate("/login")
      return
    }

    if (loadingItemId !== null) return

    setLoadingItemId(food_id)

    try {
      await axios.put(
        `${API}/cart/increment`,
        {
          food_id,
          selected_size: "250g"
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      await loadCart()
      window.dispatchEvent(new Event("srilekha-cart-updated"))
    } catch (err) {
      console.log("INCREMENT CART ERROR:", err.response?.data || err)
      alert(err.response?.data?.msg || "Could not update quantity")
    } finally {
      setLoadingItemId(null)
    }
  }

  const decrementQty = async (food_id) => {
    const token = getToken()

    if (!token) {
      navigate("/login")
      return
    }

    if (loadingItemId !== null) return

    setLoadingItemId(food_id)

    try {
      await axios.put(
        `${API}/cart/decrement`,
        {
          food_id,
          selected_size: "250g"
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      await loadCart()
      window.dispatchEvent(new Event("srilekha-cart-updated"))
    } catch (err) {
      console.log("DECREMENT CART ERROR:", err.response?.data || err)
      alert(err.response?.data?.msg || "Could not update quantity")
    } finally {
      setLoadingItemId(null)
    }
  }

  const selectedCategory = normalizeCategory(category)

  const meta = categoryMeta[selectedCategory] || {
    name: "Category",
    kicker: "Homemade Foods",
    description: "Fresh homemade food items."
  }

  const filteredFoods = foods.filter(item => {
    const itemCategory = normalizeCategory(item.category)
    const aliases = categoryAliases[selectedCategory] || []

    const matchesCategory =
      aliases.includes(itemCategory) ||
      (
        selectedCategory === "snacks" &&
        !nonSnackCategories.includes(itemCategory)
      )

    const matchesSearch = item.name
      ?.toLowerCase()
      .includes(search.toLowerCase())

    return matchesCategory && matchesSearch
  })

  const showComingSoon =
    filteredFoods.length === 0 && search.trim() === ""

  const totalCartItems = Object.values(cartItems).reduce(
    (a, b) => a + Number(b || 0),
    0
  )

  return (
    <div className="category-page">

      <section className={`category-hero category-hero-${selectedCategory}`}>
        <button
          type="button"
          className="category-back-btn"
          onClick={() => navigate("/")}
        >
          Back
        </button>

        <div className="category-hero-count">
          <b>{filteredFoods.length}</b>
          <span>Items</span>
        </div>

        <div>
          <div className="ct">
            <span>{meta.kicker}</span>
          </div>

          <h1>{meta.name}</h1>

          <p>{meta.description}</p>
        </div>
      </section>

      <div className="category-search-box">
        <input
          type="text"
          placeholder={`Search ${meta.name}...`}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="food-grid category-food-grid">

        {showComingSoon && (
          <div className="empty-category">
            <h3>Coming Soon</h3>
            <p>
              We are preparing delicious homemade items for this category.
              Please check back soon!
            </p>
          </div>
        )}

        {filteredFoods.length === 0 && search.trim() !== "" && (
          <div className="empty-category">
            <h3>No foods found</h3>
            <p>
              No items match your search. Try another name.
            </p>
          </div>
        )}

        {filteredFoods.map(item => {
          const cartKey = `${item.id}_250g`
          const orderable = isOrderable(item)
          const timerText = formatRemainingTime(item)

          return (
            <div
              key={item.id}
              className={
                orderable
                  ? "food-card"
                  : "food-card food-card-disabled"
              }
            >
              <div
                className="food-image-box"
                onClick={() => navigate(`/food/${item.id}`)}
              >
                <img
                  src={getFoodImageUrl(
                    item,
                    API,
                    "https://via.placeholder.com/300x450"
                  )}
                  alt={item.name}
                  onError={e => {
                    e.currentTarget.src = "/logo192.png"
                  }}
                />

                <span
                  className={
                    orderable
                      ? "food-status-badge"
                      : "food-status-badge out"
                  }
                >
                  {getFoodStatusLabel(item)}
                </span>

                {timerText && (
                  <span className="food-timer-badge">
                    {timerText}
                  </span>
                )}

                <div className="food-title-bottom">
                  <div className="food-price">
                    ₹ {item.price}
                  </div>

                  <h2>{item.name}</h2>

                  <p>
                    {orderable
                      ? "View Details"
                      : getFoodStatusLabel(item)}
                  </p>
                </div>
              </div>

              {cartItems[cartKey] ? (
                <div className="cart-control-bar">
                  <button
                    type="button"
                    onClick={() => decrementQty(item.id)}
                    disabled={loadingItemId === item.id}
                  >
                    -
                  </button>

                  <span>
                    {loadingItemId === item.id
                      ? "Updating..."
                      : `Added ${cartItems[cartKey]}`}
                  </span>

                  <button
                    type="button"
                    onClick={() => incrementQty(item.id)}
                    disabled={
                      loadingItemId === item.id ||
                      !orderable
                    }
                  >
                    +
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="add-cart-full-btn"
                  onClick={() => addToCart(item.id)}
                  disabled={
                    loadingItemId === item.id ||
                    !orderable
                  }
                >
                  {!orderable
                    ? getFoodStatusLabel(item)
                    : loadingItemId === item.id
                      ? "Adding..."
                      : "Add To Cart"}
                </button>
              )}
            </div>
          )
        })}

      </div>

      {totalCartItems > 0 && (
        <div className="floating-cart">
          <span>🛒 {totalCartItems}</span>

          <button
            type="button"
            onClick={() => navigate("/cart")}
          >
            Go To Cart
          </button>
        </div>
      )}

    </div>
  )
}

export default CategoryPage
